import { useMemo, useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  CalendarDays,
  Receipt,
  DollarSign,
  Clock,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Send,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { StandaloneSendDialog } from "@/pages/IntakeForms";
import DateRangeFilter, {
  PRESETS,
  presetRange,
  ymd,
} from "@/components/DateRangeFilter";
import InvoiceReconcileBanner from "@/components/InvoiceReconcileBanner";
import MyOpenTasksWidget from "@/components/MyOpenTasksWidget";
import MyEnquiriesWidget from "@/components/MyEnquiriesWidget";
import PendingAcknowledgementsWidget from "@/components/PendingAcknowledgementsWidget";
import PendingAnnouncementsWidget from "@/components/PendingAnnouncementsWidget";
import TodayDashboardPanels from "@/components/TodayDashboardPanels";
import LearningWidget from "@/components/dashboard/LearningWidget";
import { fmtMoney, fmtDateTime } from "@/lib/intl";

function StatCard({ label, value, hint, icon: Icon, testId, mono = true }) {
  return (
    <Card
      data-testid={testId}
      className="p-6 rounded-lg border border-zinc-200 shadow-none hover:border-zinc-300 transition-colors bg-white"
    >
      <div className="flex items-start justify-between">
        <div className="text-2xs sm:text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {label}
        </div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400" strokeWidth={1.75} />}
      </div>
      <div
        className={`mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-zinc-950 ${
          mono ? "font-mono-num" : "font-display"
        }`}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { status } = useOutletContext();

  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(() => presetRange("this_month"));

  const [minDate, maxDate] = range;
  const minStr = ymd(minDate);
  const maxStr = ymd(maxDate);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState(null);
  const [sendOnboardingOpen, setSendOnboardingOpen] = useState(false);

  function fetchStats() {
    if (!status.configured) return;
    if (!minStr || !maxStr) return;
    setLoading(true);
    setError(null);
    api
      .get("/dashboard/stats", { params: { minDate: minStr, maxDate: maxStr } })
      .then((r) => { setStats(r.data); setHasFetched(true); })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }

  // No auto-fetch on mount — wait for the user to click Load.

  const hintRange = useMemo(
    () =>
      minDate && maxDate
        ? `${format(minDate, "dd-MMM-yyyy")} → ${format(maxDate, "dd-MMM-yyyy")}`
        : "—",
    [minDate, maxDate]
  );

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Overview
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
            {PRESETS[preset]}
          </h1>
          <p
            className="mt-3 max-w-xl text-sm text-zinc-600 leading-relaxed"
            data-testid="dashboard-window-hint"
          >
            Live snapshot of bookings and product orders fetched from your Acuity
            account · <span className="font-mono-num text-zinc-900">{hintRange}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setSendOnboardingOpen(true)}
            data-testid="dashboard-cta-send-onboarding"
            title="Email an onboarding / intake form to a client"
          >
            <Send className="h-4 w-4 mr-1.5" /> Send onboarding form
          </Button>
          <Link to="/appointments">
            <Button
              variant="outline"
              className="border-zinc-300 hover:bg-zinc-100"
              data-testid="dashboard-cta-appointments"
            >
              View bookings <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
          <Link to="/orders">
            <Button
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="dashboard-cta-orders"
            >
              View orders <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>

      <TodayDashboardPanels />

      {/* Range selector */}
      {status.configured && (
        <>
          <InvoiceReconcileBanner status={status} />
          <PendingAnnouncementsWidget />
          <PendingAcknowledgementsWidget />
          <LearningWidget />
          <MyEnquiriesWidget />
          <MyOpenTasksWidget />
          <Card className="p-4 sm:p-5 rounded-lg border border-zinc-200 shadow-none">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1">
                <DateRangeFilter
                  preset={preset}
                  setPreset={setPreset}
                  range={range}
                  setRange={setRange}
                  idPrefix="dashboard-range"
                />
              </div>
              <Button
                onClick={fetchStats}
                disabled={loading}
                data-testid="dashboard-load-btn"
                className="bg-zinc-950 hover:bg-zinc-800 text-white self-start sm:self-end"
              >
                <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                {hasFetched ? "Refresh" : "Load"}
              </Button>
            </div>
          </Card>
        </>
      )}

      {!status.configured && (
        <Card
          data-testid="not-connected-card"
          className="p-10 sm:p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50"
        >
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="hidden md:block w-40 h-40 grid-bg rounded-md border border-zinc-200" />
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-md text-xs font-medium text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" /> Acuity not connected
              </div>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950">
                Connect your Acuity account to begin reporting
              </h2>
              <p className="mt-2 text-sm text-zinc-600 max-w-xl leading-relaxed">
                Enter your Acuity User ID and API Key in Settings. We'll then pull
                appointments, product orders and revenue figures here.
              </p>
              <Link to="/settings">
                <Button
                  className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid="connect-acuity-cta"
                >
                  Open Settings <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {status.configured && (
        <>
          {error && (
            <div
              data-testid="dashboard-error"
              className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {!hasFetched && !loading && (
            <Card
              data-testid="dashboard-empty"
              className="p-10 sm:p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50"
            >
              <div className="flex flex-col items-center text-center max-w-md mx-auto">
                <div className="h-12 w-12 grid place-items-center rounded-full bg-white border border-zinc-200">
                  <CalendarDays className="h-5 w-5 text-zinc-500" />
                </div>
                <h2 className="mt-4 font-display text-xl font-semibold text-zinc-950">
                  Pick a range, then load
                </h2>
                <p className="mt-2 text-sm text-zinc-600">
                  Acuity calls can take a moment, so the dashboard waits for you to choose a window
                  and click <span className="font-medium text-zinc-900">Load</span> before pulling data.
                </p>
              </div>
            </Card>
          )}

          {hasFetched && (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Appointments"
              value={loading ? "—" : stats?.appointments_count ?? 0}
              hint={hintRange}
              icon={CalendarDays}
              testId="stat-appointments"
            />
            <StatCard
              label="Product orders"
              value={loading ? "—" : stats?.orders_count ?? 0}
              hint={hintRange}
              icon={Receipt}
              testId="stat-orders"
            />
            <StatCard
              label="Total revenue"
              value={loading ? "—" : fmtMoney(stats?.total_revenue)}
              hint="Appointments + orders"
              icon={DollarSign}
              testId="stat-revenue"
            />
            <StatCard
              label="Today"
              value={loading ? "—" : stats?.upcoming_today ?? 0}
              hint="Bookings on today"
              icon={Clock}
              testId="stat-today"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                <TrendingUp className="h-3.5 w-3.5" /> Revenue split · {hintRange}
              </div>
              <div className="mt-4 space-y-3">
                <RevenueRow
                  label="Appointments"
                  amount={stats?.appointments_revenue}
                  total={stats?.total_revenue}
                />
                <RevenueRow
                  label="Product orders"
                  amount={stats?.orders_revenue}
                  total={stats?.total_revenue}
                />
              </div>
            </Card>

            <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                Account
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <Row label="Acuity User ID" value={status.user_id || "—"} />
                <Row
                  label="Last verified"
                  value={
                    status.last_verified
                      ? fmtDateTime(status.last_verified)
                      : "Never"
                  }
                />
                <Row
                  label="Canceled (window)"
                  value={loading ? "—" : stats?.canceled_count ?? 0}
                />
              </div>
              <Link to="/settings">
                <Button
                  variant="outline"
                  className="mt-5 border-zinc-300 hover:bg-zinc-100"
                  data-testid="manage-credentials-btn"
                >
                  Manage credentials
                </Button>
              </Link>
            </Card>
          </div>
          </>
          )}
        </>
      )}

      {/* Onboarding-form send dialog — mounted at the dashboard root
         so it can be opened from any CTA. Reuses the exact same
         dialog / send flow the Onboarding page uses so behaviour
         stays consistent (server-side, this hits the standalone
         send route). */}
      <StandaloneSendDialog
        open={sendOnboardingOpen}
        prefill={null}
        onClose={() => setSendOnboardingOpen(false)}
        onSent={() => setSendOnboardingOpen(false)}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 pb-2 last:border-0">
      <span className="text-zinc-500">{label}</span>
      <span className="font-mono-num text-zinc-950">{value}</span>
    </div>
  );
}

function RevenueRow({ label, amount, total }) {
  const a = Number(amount || 0);
  const t = Number(total || 0);
  const pct = t > 0 ? (a / t) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-700">{label}</span>
        <span className="font-mono-num text-zinc-950">{fmtMoney(amount)}</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-zinc-950"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
}


